/**
 * @file src/components/atoms/Text/Text.tsx
 * @description This file defines the Text component, a flexible and reusable component for rendering text elements with configurable intent, size, color, and other style properties. It utilizes the class-variance-authority (CVA) library to manage variant styles consistently across different text types.
 * @module Text
 */

import { cva, VariantProps } from 'class-variance-authority';
import { FC, PropsWithChildren } from 'react';

const textVariants = cva('', {
  variants: {
    intent: {
      text: [''],
      paragraph: ['block'],
      link: [''],
      mono: ['font-mono'],
    },
    color: {
      primary: ['text-primary'],
      secondary: ['text-secondary'],
      tertiary: ['text-secondary/70'],
      accent: ['text-accent'],
      ai: ['text-ai'],
      success: ['text-success'],
      error: ['text-error'],
      warning: ['text-warning'],
      disabled: ['text-disabled'],
      transparent: ['text-secondary'],
      loading: ['text-primary'],
    },
    size: {
      xsm: ['text-xsm'],
      sm: ['text-sm'],
      base: ['text-base'],
      subtitle: ['text-subtitle'],
      md: ['text-md'],
      h4: ['text-h4'],
      h3: ['text-h3'],
      h2: ['text-h2'],
      h1: ['text-h1'],
      lg: ['text-lg'],
      overline: ['text-overline'],
      caption: ['text-caption'],
      label: ['text-label'],
      callout: ['text-callout'],
      subheading: ['text-subheading'],
      body: ['text-body'],
      heading: ['text-heading'],
      title3: ['text-title3'],
      title2: ['text-title2'],
      title1: ['text-title1'],
      display2: ['text-display2'],
      display1: ['text-display1'],
    },
    disabled: {
      true: ['cursor-default'],
    },
  },
  compoundVariants: [
    { intent: 'link', className: 'text-accent text-sm' },
    {
      intent: 'link',
      disabled: false,
      className: 'cursor-pointer',
    },
    {
      intent: 'link',
      disabled: true,
      className: 'text-accent/25 cursor-default',
    },
  ],
  defaultVariants: {
    intent: 'text',
    size: 'body',
    color: 'secondary',
  },
});

type TextVariants = VariantProps<typeof textVariants>;

type TextProps = {
  id?: string;
  value?: string;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
} & PropsWithChildren<TextVariants>;

const formatTextWithNewlines = (text: string) => {
  return text.split('\n').map((line, index) => (
    <span key={index}>
      {line}
      <br />
    </span>
  ));
};

const Text: FC<TextProps> = ({
  id,
  value,
  children,
  intent,
  size,
  color,
  className,
  disabled = false,
  onClick,
}) => {
  return (
    <span
      id={id}
      onClick={disabled ? () => {} : onClick}
      className={textVariants({
        intent: onClick ? 'link' : intent,
        size,
        color,
        disabled,
        className,
      })}
    >
      {children ?? (value ? formatTextWithNewlines(value) : null)}
    </span>
  );
};

export default Text;
export { textVariants };
export type { TextProps, TextVariants };
